import { RARE_ITEMS } from "@redchain/contracts";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiRequestError } from "../api/client";
import { sseBus } from "../api/events";
import type { ItemRow } from "../api/types";
import { usePayFeeIn } from "../components/ActionStatus";
import { formatRcd, formatSol, solToLamports } from "../overlay/format";
import { useWalletAction } from "../overlay/useWalletAction";
import { usePlayer } from "../state/PlayerContext";
import { t } from "../strings";
import { signBuilt } from "../wallet/actions";
import { useWallet } from "../wallet/WalletProvider";
import "./gen1.css";
import { useGen1Nav } from "./useGen1Nav";
import { Marketplace, type MarketplaceListing, type MarketplacePokemon } from "./Marketplace";
import { RcdMarket, type RcdMarketPreview } from "./RcdMarket";

type Screen =
  | { id: "root" }
  | { id: "rcd" }
  | { id: "rcdAction"; action: "claim" | "deposit" | "limit" }
  | { id: "items" }
  | { id: "itemDetail"; index: number }
  | { id: "vault" }
  | { id: "vaultAction"; action: "add" | "withdraw" }
  | { id: "fees" }
  | { id: "shop" | "marketplace" | "rcdMarket" }
  | { id: "confirm"; message: string; onYes: () => void }
  | { id: "message"; text: string; ok?: boolean };

export interface ShopListing {
  productId: string;
  name: string;
  priceRcd: string;
  version: number;
  stock: number;
}

const EMPTY_SHOP_STOCK: readonly ShopListing[] = [];
const EMPTY_MARKET_LISTINGS: readonly MarketplaceListing[] = [];
const EMPTY_MARKET_POKEMON: readonly MarketplacePokemon[] = [];

function commerceReason(reason: string): string {
  switch (reason) {
    case "active_session":
      return "CLOSE GAME FIRST";
    case "sync_pending":
      return "WAIT FOR SAVE";
    case "listing_unavailable":
      return "LISTING GONE";
    case "product_unavailable":
      return "SOLD OUT";
    case "insufficient_balance":
      return "NOT ENOUGH ₽";
    case "quote_expired":
      return "QUOTE EXPIRED";
    default:
      return reason.toUpperCase().replace(/_/g, " ");
  }
}

function MenuList({
  items,
  index,
  onPick,
  prices,
}: {
  items: string[];
  index: number;
  onPick: (i: number) => void;
  prices?: readonly (string | undefined)[];
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const previousIndex = useRef(index);

  useEffect(() => {
    const moved = previousIndex.current !== index;
    previousIndex.current = index;
    const selected = listRef.current?.children[index];
    const viewport = listRef.current?.closest<HTMLElement>(".wallet-menu__content");
    if (!selected || !viewport) return;
    // Show detail fields first; list screens always reveal their cursor.
    if (!moved && viewport.closest<HTMLElement>(".wallet-menu__lcd")?.dataset.screen === "itemDetail") return;
    const row = selected.getBoundingClientRect();
    const frame = viewport.getBoundingClientRect();
    if (row.bottom > frame.bottom) viewport.scrollTop += row.bottom - frame.bottom;
    else if (row.top < frame.top) viewport.scrollTop -= frame.top - row.top;
  }, [index, items]);

  return (
    <ul ref={listRef} className="wallet-menu__list" role="listbox">
      {items.map((label, i) => (
        <li key={label} role="option" aria-selected={i === index} onClick={() => onPick(i)}>
          <span className="wallet-menu__cursor" aria-hidden="true">
            {i === index ? "\u25b6" : "\u00a0"}
          </span>
          <span className="wallet-menu__label">{label}</span>
          {prices?.[i] !== undefined && <span className="wallet-menu__price">{prices[i]}</span>}
        </li>
      ))}
    </ul>
  );
}

function StatusLine({ action }: { action: ReturnType<typeof useWalletAction> }) {
  if (action.waiting) return <p className="wallet-menu__status">Waiting for sync…</p>;
  if (action.busy) return <p className="wallet-menu__status">Signing…</p>;
  if (action.error) return <p className="wallet-menu__status">{action.error}</p>;
  if (action.result) return <p className="wallet-menu__status ok">Done.</p>;
  return <p className="wallet-menu__status" />;
}

function AmountScreen({
  title,
  hint,
  value,
  setValue,
  decimal,
  onConfirm,
  onBack,
  action,
  feeToggle,
}: {
  title: string;
  hint?: string;
  value: string;
  setValue: (v: string) => void;
  decimal?: boolean;
  onConfirm: () => void;
  onBack: () => void;
  action: ReturnType<typeof useWalletAction>;
  feeToggle?: { label: string; onToggle: () => void };
}) {
  const items = useMemo(() => ["CONFIRM", ...(feeToggle ? [feeToggle.label] : []), "CANCEL"], [feeToggle]);
  const onA = useCallback(
    (i: number) => {
      if (items[i] === "CONFIRM") onConfirm();
      else if (items[i] === "CANCEL") onBack();
      else feeToggle?.onToggle();
    },
    [items, onConfirm, onBack, feeToggle],
  );
  const { index, setIndex } = useGen1Nav(items.length, onA, onBack);

  return (
    <>
      <h2 className="wallet-menu__title">{title}</h2>
      {hint && <p className="wallet-menu__hint">{hint}</p>}
      <div className="wallet-menu__amount">
        <label>
          Amount{" "}
          <input
            autoFocus
            inputMode={decimal ? "decimal" : "numeric"}
            value={value}
            onChange={(e) => setValue(decimal ? e.target.value.replace(/[^\d.]/g, "") : e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setIndex(0);
                onConfirm();
              }
            }}
          />
        </label>
      </div>
      <MenuList items={items} index={index} onPick={onA} />
      <StatusLine action={action} />
    </>
  );
}

/**
 * Gen1-styled Wallet UI over the paused LCD. Opened from Start → WALLET.
 */
export function WalletMenu({ onClose, shopStock: shopStockProp, marketListings: marketListingsProp, marketPokemon: marketPokemonProp, rcdMarketPreview }: {
  onClose: () => void;
  shopStock?: readonly ShopListing[];
  marketListings?: readonly MarketplaceListing[];
  marketPokemon?: readonly MarketplacePokemon[];
  rcdMarketPreview?: RcdMarketPreview;
}) {
  const { me, inSession, refresh } = usePlayer();
  const { wallet } = useWallet();
  const [screen, setScreen] = useState<Screen>({ id: "root" });
  const [amount, setAmount] = useState("");
  const [items, setItems] = useState<ItemRow[] | null>(null);
  const [asset, setAsset] = useState("");
  const [shopStock, setShopStock] = useState<readonly ShopListing[]>(shopStockProp ?? EMPTY_SHOP_STOCK);
  const [marketListings, setMarketListings] = useState<readonly MarketplaceListing[]>(marketListingsProp ?? EMPTY_MARKET_LISTINGS);
  const [marketPokemon, setMarketPokemon] = useState<readonly MarketplacePokemon[]>(marketPokemonProp ?? EMPTY_MARKET_POKEMON);
  const [walletSolLamports, setWalletSolLamports] = useState("0");
  const [shopStatus, setShopStatus] = useState("");
  const fee = usePayFeeIn();
  const payFeeIn = fee.value;
  const claim = useWalletAction();
  const deposit = useWalletAction();
  const allowance = useWalletAction();
  const itemAction = useWalletAction();
  const vaultAdd = useWalletAction();
  const vaultWithdraw = useWalletAction();

  const loadItems = useCallback(async () => {
    try {
      setItems((await api.items()).items);
    } catch {
      setItems([]);
    }
  }, []);

  const loadCommerce = useCallback(async () => {
    if (shopStockProp && marketListingsProp) return;
    try {
      const [catalog, listings, eligible] = await Promise.all([
        api.shopCatalog().catch(() => ({ products: [] })),
        api.marketListings().catch(() => ({ listings: [], cursor: null })),
        api.marketEligibleAssets().catch(() => ({ assets: [] })),
      ]);
      setShopStock(
        catalog.products.map((p) => ({
          productId: p.productId,
          name: p.name,
          priceRcd: p.priceRcd,
          version: p.version,
          stock: p.stock,
        })),
      );
      setMarketListings(
        listings.listings.map((l) => ({
          id: l.listingId,
          listingId: l.listingId,
          name: l.nickname || `No.${l.species ?? "?"}`,
          image: l.imageUrl ?? "",
          details: l.details ?? (l.level != null ? [`Lv${l.level}`] : []),
          priceRcd: l.priceRcd,
          kind: "pokemon" as const,
          seller: l.seller.slice(0, 4),
        })),
      );
      setMarketPokemon(
        eligible.assets
          .filter((a) => a.eligible)
          .map((a) => ({
            id: a.asset,
            name: a.nickname || `No.${a.species ?? "?"}`,
            image: a.imageUrl ?? "",
            details: a.details,
          })),
      );
    } catch {
      /* keep empty */
    }
  }, [shopStockProp, marketListingsProp]);

  useEffect(() => {
    void loadItems();
    void loadCommerce();
    const offs = (["items.changed", "item.claimed", "item.deposited", "chain.committed", "market.listing.updated", "shop.purchase.updated"] as const).map((type) =>
      sseBus.on(type, () => {
        void loadItems();
        void loadCommerce();
      }),
    );
    return () => offs.forEach((off) => off());
  }, [loadItems, loadCommerce]);

  useEffect(() => {
    if (!wallet) return;
    void (async () => {
      try {
        const { createSolanaRpc, address } = await import("@solana/kit");
        const { rpcUrl } = await import("../env");
        const bal = await createSolanaRpc(rpcUrl).getBalance(address(wallet.address)).send();
        setWalletSolLamports(String(bal.value));
      } catch {
        setWalletSolLamports("0");
      }
    })();
  }, [wallet, me?.walletRcd]);

  const goRoot = useCallback(() => {
    setScreen({ id: "root" });
    setAmount("");
    claim.clearError();
    deposit.clearError();
    allowance.clearError();
    itemAction.clearError();
    vaultAdd.clearError();
    vaultWithdraw.clearError();
  }, [claim, deposit, allowance, itemAction, vaultAdd, vaultWithdraw]);

  const rootItems = useMemo(() => ["RCD", "ITEMS", "SHOP", "MARKET", "RCD MKT", "VAULT", "FEES", "EXIT"], []);
  const rootOnA = useCallback(
    (i: number) => {
      const label = rootItems[i];
      if (label === "EXIT") onClose();
      else if (label === "RCD") setScreen({ id: "rcd" });
      else if (label === "ITEMS") setScreen({ id: "items" });
      else if (label === "SHOP") setScreen({ id: "shop" });
      else if (label === "MARKET") setScreen({ id: "marketplace" });
      else if (label === "RCD MKT") setScreen({ id: "rcdMarket" });
      else if (label === "VAULT") setScreen({ id: "vault" });
      else if (label === "FEES") setScreen({ id: "fees" });
    },
    [rootItems, onClose],
  );
  const rootNav = useGen1Nav(screen.id === "root" ? rootItems.length : 0, rootOnA, onClose);

  const rcdMenu = useMemo(() => ["CLAIM", "DEPOSIT", "SET LIMIT", "COPY ADDRESS", "BACK"], []);
  const rcdOnA = useCallback(
    (i: number) => {
      const label = rcdMenu[i];
      if (label === "BACK") goRoot();
      else if (label === "COPY ADDRESS" && me) {
        void navigator.clipboard.writeText(me.depositAddress.address).then(() => setScreen({ id: "message", text: "Copied!", ok: true }));
      } else if (label === "CLAIM") {
        setAmount("");
        setScreen({ id: "rcdAction", action: "claim" });
      } else if (label === "DEPOSIT") {
        setAmount("");
        setScreen({ id: "rcdAction", action: "deposit" });
      } else if (label === "SET LIMIT") {
        setAmount("");
        setScreen({ id: "rcdAction", action: "limit" });
      }
    },
    [rcdMenu, goRoot, me],
  );
  const rcdNav = useGen1Nav(screen.id === "rcd" ? rcdMenu.length : 0, rcdOnA, goRoot);

  const playing = inSession || !!me?.activeSession;
  const ownedItems = useMemo(() => (items ?? []).filter((row) => row.wallet > 0 || row.inGame + row.credit - row.debt > 0), [items]);
  const itemNames = useMemo(() => {
    return [...ownedItems.map((row) => row.name || RARE_ITEMS[row.index]?.name || `ITEM ${row.index}`), "BACK"];
  }, [ownedItems]);
  const itemsOnA = useCallback(
    (i: number) => {
      if (i >= ownedItems.length) {
        goRoot();
        return;
      }
      const row = ownedItems[i]!;
      setAmount("1");
      setAsset("");
      setScreen({ id: "itemDetail", index: row.index });
    },
    [ownedItems, goRoot],
  );
  const itemsNav = useGen1Nav(screen.id === "items" ? itemNames.length : 0, itemsOnA, goRoot);

  const itemDetailMenu = useMemo(() => {
    if (playing) return ["BACK"];
    return ["CLAIM", "DEPOSIT", ...(payFeeIn === "RCD" ? ["FEE: RCD"] : ["FEE: SOL"]), "BACK"];
  }, [playing, payFeeIn]);
  const itemDetailOnA = useCallback(
    async (i: number) => {
      const label = itemDetailMenu[i];
      if (label === "BACK" || screen.id !== "itemDetail") {
        setScreen({ id: "items" });
        return;
      }
      if (label?.startsWith("FEE:")) {
        fee.onChange(payFeeIn === "RCD" ? "SOL" : "RCD");
        return;
      }
      const row = items?.find((x) => x.index === screen.index);
      if (!row) return;
      const n = Number(amount) || 1;
      if (label === "CLAIM") {
        if (await itemAction.run(() => api.itemsClaim(row.index, n, payFeeIn))) {
          void loadItems();
          setScreen({ id: "message", text: "Claimed!", ok: true });
        }
      } else if (label === "DEPOSIT") {
        if (row.index === 0 && !asset) {
          setScreen({ id: "message", text: "Need Master Ball address." });
          return;
        }
        if (await itemAction.run(() => api.itemsDeposit(row.index, n, payFeeIn, row.index === 0 ? asset : undefined))) {
          void loadItems();
          setScreen({ id: "message", text: "Deposited!", ok: true });
        }
      }
    },
    [itemDetailMenu, screen, items, amount, asset, payFeeIn, fee, itemAction, loadItems],
  );
  const itemDetailNav = useGen1Nav(screen.id === "itemDetail" ? itemDetailMenu.length : 0, itemDetailOnA, () => setScreen({ id: "items" }));

  const vaultMenu = useMemo(() => ["ADD SOL", "WITHDRAW", "BACK"], []);
  const vaultOnA = useCallback(
    (i: number) => {
      const label = vaultMenu[i];
      if (label === "BACK") goRoot();
      else if (label === "ADD SOL") {
        setAmount("");
        setScreen({ id: "vaultAction", action: "add" });
      } else if (label === "WITHDRAW") {
        setAmount("");
        setScreen({ id: "vaultAction", action: "withdraw" });
      }
    },
    [vaultMenu, goRoot],
  );
  const vaultNav = useGen1Nav(screen.id === "vault" ? vaultMenu.length : 0, vaultOnA, goRoot);

  const feesNav = useGen1Nav(screen.id === "fees" ? 1 : 0, goRoot, goRoot);
  const shopOptions = useMemo(() => [...shopStock.map((listing) => listing.name), "CANCEL"], [shopStock]);
  const shopPrices = useMemo(() => shopStock.map((listing) => formatRcd(listing.priceRcd)), [shopStock]);
  const shopOnA = useCallback(
    (i: number) => {
      if (i >= shopStock.length) {
        goRoot();
        return;
      }
      const product = shopStock[i]!;
      if (!wallet) {
        setShopStatus("LOG IN FIRST");
        return;
      }
      setShopStatus("SIGNING…");
      void (async () => {
        try {
          const idem = `shop-${product.productId}-${Date.now()}`;
          const built = await api.shopPurchase(product.productId, product.version, idem);
          const { base64 } = await signBuilt(wallet, built);
          await api.shopSubmit(built.intentId, base64);
          setShopStatus("BOUGHT");
          void refresh();
          void loadCommerce();
        } catch (e) {
          setShopStatus(e instanceof ApiRequestError ? commerceReason(e.reason) : "FAILED");
        }
      })();
    },
    [shopStock, goRoot, wallet, refresh, loadCommerce],
  );
  const shopNav = useGen1Nav(screen.id === "shop" ? shopOptions.length : 0, shopOnA, goRoot);
  const confirmNav = useGen1Nav(
    screen.id === "confirm" ? 2 : 0,
    (i) => {
      if (screen.id !== "confirm") return;
      if (i === 0) screen.onYes();
      else setScreen({ id: "rcd" });
    },
    () => setScreen({ id: "rcd" }),
  );
  const messageNav = useGen1Nav(
    screen.id === "message" ? 1 : 0,
    () => setScreen({ id: "root" }),
    () => setScreen({ id: "root" }),
  );

  if (!me) {
    return (
      <div className="wallet-menu" role="dialog" aria-label="Wallet">
        <div className="wallet-menu__lcd wallet-menu__lcd--full">
          <div className="wallet-menu__box">
            <div className="wallet-menu__content">
              <p className="wallet-menu__hint">Log in first.</p>
            </div>
          </div>
          <p className="wallet-menu__footer">B / ESC: close</p>
        </div>
      </div>
    );
  }

  const remaining = BigInt(me.claim.remainingToday) < BigInt(me.claim.globalRemainingToday) ? me.claim.remainingToday : me.claim.globalRemainingToday;

  const runClaim = async () => {
    const n = Number(amount);
    if (!Number.isInteger(n) || n <= 0) return;
    const doClaim = async () => {
      const r = await claim.run(() => api.rcdClaim(n, playing));
      if (r) {
        setAmount("");
        void refresh();
        setScreen({ id: "message", text: "Claimed!", ok: true });
      }
    };
    if (playing) {
      setScreen({
        id: "confirm",
        message: t.midSessionClaim,
        onYes: () => void doClaim(),
      });
      return;
    }
    await doClaim();
  };

  const runDeposit = async () => {
    const n = Number(amount);
    if (!Number.isInteger(n) || n <= 0) return;
    const r = await deposit.run(() => api.rcdDeposit(n, payFeeIn));
    if (r) {
      setAmount("");
      void refresh();
      setScreen({ id: "message", text: "Deposited!", ok: true });
    }
  };

  const runLimit = async () => {
    const n = Number(amount);
    if (!Number.isInteger(n) || n < 0) return;
    const r = await allowance.run(() => api.rcdAllowance(n, payFeeIn));
    if (r) {
      setAmount("");
      void refresh();
      setScreen({ id: "message", text: "Limit set!", ok: true });
    }
  };

  const runVaultAdd = async () => {
    try {
      const lamports = amount ? solToLamports(amount) : me.vault.topUpNeeded;
      if (!lamports || BigInt(lamports) <= 0n) throw new Error("Enter SOL");
      if (await vaultAdd.run(() => api.vaultDeposit(lamports))) {
        setAmount("");
        void refresh();
        setScreen({ id: "message", text: "Added SOL!", ok: true });
      }
    } catch (e) {
      setScreen({ id: "message", text: e instanceof Error ? e.message : String(e) });
    }
  };

  const runVaultWithdraw = async () => {
    try {
      const lamports = BigInt(solToLamports(amount));
      const after = BigInt(me.vault.lamports) - lamports;
      if (after < 0n) throw new Error("Too much.");
      if (after > 0n && after < BigInt(me.vault.minimum)) {
        throw new Error(`Leave 0 or ≥ ${formatSol(me.vault.minimum)} SOL`);
      }
      if (await vaultWithdraw.run(() => api.vaultWithdraw(lamports.toString(), payFeeIn))) {
        setAmount("");
        void refresh();
        setScreen({ id: "message", text: "Withdrew!", ok: true });
      }
    } catch (e) {
      setScreen({ id: "message", text: e instanceof Error ? e.message : String(e) });
    }
  };

  let body: ReactNode = null;
  if (screen.id === "root") {
    body = (
      <>
        <h2 className="wallet-menu__title">WALLET</h2>
        <MenuList items={rootItems} index={rootNav.index} onPick={rootOnA} />
      </>
    );
  } else if (screen.id === "rcd") {
    body = (
      <>
        <h2 className="wallet-menu__title">RCD</h2>
        <dl className="wallet-menu__kv">
          <dt>In game</dt>
          <dd>{formatRcd(me.gameBalance)}</dd>
          <dt>In wallet</dt>
          <dd>{formatRcd(me.walletRcd)}</dd>
          <dt>Claimable</dt>
          <dd>{me.claim.paused ? "Paused" : formatRcd(remaining)}</dd>
          <dt>Limit</dt>
          <dd>{formatRcd(me.walletAllowance.approved)}</dd>
        </dl>
        <MenuList items={rcdMenu} index={rcdNav.index} onPick={rcdOnA} />
      </>
    );
  } else if (screen.id === "rcdAction") {
    const titles = { claim: "CLAIM RCD", deposit: "DEPOSIT RCD", limit: "SET LIMIT" } as const;
    const runners = { claim: runClaim, deposit: runDeposit, limit: runLimit } as const;
    const actions = { claim, deposit, limit: allowance } as const;
    body = (
      <AmountScreen
        title={titles[screen.action]}
        value={amount}
        setValue={setAmount}
        onConfirm={() => void runners[screen.action]()}
        onBack={() => setScreen({ id: "rcd" })}
        action={actions[screen.action]}
        feeToggle={
          screen.action === "claim"
            ? undefined
            : {
                label: payFeeIn === "RCD" ? "FEE: RCD" : "FEE: SOL",
                onToggle: () => fee.onChange(payFeeIn === "RCD" ? "SOL" : "RCD"),
              }
        }
      />
    );
  } else if (screen.id === "items") {
    body = (
      <>
        <h2 className="wallet-menu__title">ITEMS</h2>
        {items === null && <p className="wallet-menu__hint">{t.walletItemsLoading}</p>}
        {items !== null && ownedItems.length === 0 && <p className="wallet-menu__hint">{t.walletItemsEmpty}</p>}
        {playing && ownedItems.length > 0 && <p className="wallet-menu__hint">{t.itemsDuringPlay}</p>}
        <MenuList items={itemNames} index={itemsNav.index} onPick={itemsOnA} />
      </>
    );
  } else if (screen.id === "itemDetail") {
    const row = items?.find((x) => x.index === screen.index);
    const name = row?.name || RARE_ITEMS[screen.index]?.name || `Item ${screen.index}`;
    body = (
      <>
        <h2 className="wallet-menu__title">{name}</h2>
        {row && (
          <dl className="wallet-menu__kv">
            <dt>In game</dt>
            <dd>{Math.max(0, row.inGame + row.credit - row.debt)}</dd>
            <dt>Wallet</dt>
            <dd>{row.wallet}</dd>
            <dt>Left</dt>
            <dd>{Math.max(0, row.cap - row.foundThisGeneration)}</dd>
          </dl>
        )}
        {!playing && (
          <div className="wallet-menu__amount">
            <label>
              Qty{" "}
              <input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, "") || "1")} />
            </label>
            {screen.index === 0 && (
              <label>
                Asset{" "}
                <input value={asset} onChange={(e) => setAsset(e.target.value.trim())} placeholder="Master Ball addr" />
              </label>
            )}
          </div>
        )}
        <MenuList items={itemDetailMenu} index={itemDetailNav.index} onPick={(i) => void itemDetailOnA(i)} />
        <StatusLine action={itemAction} />
      </>
    );
  } else if (screen.id === "vault") {
    body = (
      <>
        <h2 className="wallet-menu__title">VAULT</h2>
        <dl className="wallet-menu__kv">
          <dt>Balance</dt>
          <dd>{formatSol(me.vault.lamports)} SOL</dd>
          <dt>Locked</dt>
          <dd>{formatSol(me.vault.refundableLockedLamports)} SOL</dd>
          {me.vault.pendingMints > 0 && (
            <>
              <dt>Pending</dt>
              <dd>{me.vault.pendingMints} mons</dd>
            </>
          )}
        </dl>
        <MenuList items={vaultMenu} index={vaultNav.index} onPick={vaultOnA} />
      </>
    );
  } else if (screen.id === "vaultAction") {
    body = (
      <AmountScreen
        title={screen.action === "add" ? "ADD SOL" : "WITHDRAW"}
        hint={screen.action === "add" && BigInt(me.vault.topUpNeeded) > 0n ? `Need ${formatSol(me.vault.topUpNeeded)} SOL` : undefined}
        value={amount}
        setValue={setAmount}
        decimal
        onConfirm={() => void (screen.action === "add" ? runVaultAdd() : runVaultWithdraw())}
        onBack={() => setScreen({ id: "vault" })}
        action={screen.action === "add" ? vaultAdd : vaultWithdraw}
        feeToggle={
          screen.action === "withdraw"
            ? {
                label: payFeeIn === "RCD" ? "FEE: RCD" : "FEE: SOL",
                onToggle: () => fee.onChange(payFeeIn === "RCD" ? "SOL" : "RCD"),
              }
            : undefined
        }
      />
    );
  } else if (screen.id === "fees") {
    body = (
      <>
        <h2 className="wallet-menu__title">FEES</h2>
        <div className="wallet-menu__text" role="region" aria-label="Wallet fees" tabIndex={0}>
          <dl className="wallet-menu__kv">
            <dt>Save</dt>
            <dd>{formatRcd(me.fees.perSave)}</dd>
            <dt>Auto-save</dt>
            <dd>{formatRcd(me.fees.perAutosave)}</dd>
            <dt>New Pokémon</dt>
            <dd>{formatRcd(me.fees.perMint)}</dd>
            <dt>Wallet</dt>
            <dd>{formatRcd(me.fees.perRelayedTx)}</dd>
          </dl>
          <p className="wallet-menu__hint">{t.walletFeesHelp}</p>
          {BigInt(me.feeDebtRcd) > 0n && <p className="wallet-menu__status">{t.walletFeeDebt(me.feeDebtRcd)}</p>}
        </div>
        <MenuList items={["BACK"]} index={feesNav.index} onPick={goRoot} />
      </>
    );
  } else if (screen.id === "shop") {
    body = (
      <>
        <h2 className="wallet-menu__title">SHOP</h2>
        {shopStock.length === 0 && <p className="wallet-menu__hint">{t.walletShopEmpty}</p>}
        {shopStatus && <p className="wallet-menu__status" role="status">{shopStatus}</p>}
        <MenuList
          items={shopOptions}
          prices={shopPrices}
          index={shopNav.index}
          onPick={(i) => {
            shopNav.setIndex(i);
            shopOnA(i);
          }}
        />
      </>
    );
  } else if (screen.id === "marketplace") {
    body = (
      <Marketplace
        listings={marketListings}
        ownedPokemon={marketPokemon}
        onBack={goRoot}
        onBuy={async (listing) => {
          if (!wallet || !listing.listingId) throw new Error("unavailable");
          const built = await api.marketBuy(listing.listingId, `buy-${listing.listingId}-${Date.now()}`);
          const { base64 } = await signBuilt(wallet, built);
          await api.marketSubmit(built.intentId, base64);
          void refresh();
          void loadCommerce();
        }}
        onList={async (pokemon, priceRcd) => {
          if (!wallet) throw new Error("unavailable");
          const built = await api.marketList(pokemon.id, priceRcd, `list-${pokemon.id}-${Date.now()}`);
          const { base64 } = await signBuilt(wallet, built);
          await api.marketSubmit(built.intentId, base64);
          void refresh();
          void loadCommerce();
        }}
      />
    );
  } else if (screen.id === "rcdMarket") {
    body = (
      <RcdMarket
        walletRcd={me.walletRcd}
        walletSolLamports={walletSolLamports}
        preview={rcdMarketPreview}
        onBack={goRoot}
        onDone={() => void refresh()}
      />
    );
  } else if (screen.id === "confirm") {
    body = (
      <>
        <h2 className="wallet-menu__title">OK?</h2>
        <div className="wallet-menu__text" role="region" aria-label="Claim confirmation" tabIndex={0}>
          <p className="wallet-menu__hint">{screen.message}</p>
        </div>
        <MenuList items={["YES", "NO"]} index={confirmNav.index} onPick={(i) => (i === 0 ? screen.onYes() : setScreen({ id: "rcd" }))} />
      </>
    );
  } else if (screen.id === "message") {
    body = (
      <>
        <h2 className="wallet-menu__title">{screen.ok ? "OK!" : "…"}</h2>
        <div className="wallet-menu__text" role="region" aria-label="Wallet message" tabIndex={0}>
          <p className={screen.ok ? "wallet-menu__status ok" : "wallet-menu__status"}>{screen.text}</p>
        </div>
        <MenuList items={["OK"]} index={messageNav.index} onPick={() => setScreen({ id: "root" })} />
      </>
    );
  }

  const startStyle = screen.id === "root";

  return (
    <div className="wallet-menu" role="dialog" aria-modal="true" aria-label="Wallet">
      <div className={`wallet-menu__lcd${startStyle ? "" : " wallet-menu__lcd--full"}`} data-screen={screen.id}>
        <div className="wallet-menu__box">
          <div key={screen.id} className="wallet-menu__content">{body}</div>
        </div>
      </div>
    </div>
  );
}
